/// <reference path="../../app.d.ts"/>
/// <reference path="../../../../../shared/models/generated/MitgliedschaftAnlageResponseApi.d.ts"/>

import { BasicApplicationUrlBuilder } from '../../../../../shared/services/BasicApplicationUrlBuilder';
import { ContractInfoApi } from '../../../../../shared/models/generated/PersonContractInfoApi';
import { MitgliedschaftAnlageErweiterungApi } from '../../../../../shared/models/generated/MitgliedschaftMitgliedschaftAnlageErweiterungApi';
import { MitgliedschaftGutscheinApi } from '../../../../../shared/models/generated/MitgliedschaftMitgliedschaftGutscheinApi';
import { ProfiwerberViewModelApi } from '../../../../../shared/models/generated/MitgliedschaftProfiwerberViewModelApi';
import { MitgliedschaftAnlageErweiterungProfiWerberApi } from '../../../../../shared/models/generated/MitgliedschaftMitgliedschaftAnlageErweiterungProfiWerberApi';
import { RequestModelApi } from '../../../../../shared/models/generated/WalletProxyRequestModelApi';
import { AppUtils } from '../../../../../shared/utils/index';

export class AppApiService {
	private readonly options: MglAddMembersAppOptions;
	private readonly applicationUrlBuilder: BasicApplicationUrlBuilder<MglAddMembersAppDefinition>;

	constructor(options: MglAddMembersAppOptions) {
		this.options = options;
		this.applicationUrlBuilder = new BasicApplicationUrlBuilder(options);
	}

	/**
	 * Submits all data to backend api to extend the membership.
	 * Only works for logged in users.
	 * User information should have been merged into form model by the caller (formModel.mergeWithUser(user)).
	 *
	 * /api/mitgliedschaft/erweitern
	 */
	public submitEnclosure(reqModel: MitgliedschaftAnlageErweiterungApi | MitgliedschaftAnlageErweiterungProfiWerberApi,
		successCallback: (data: ApiResponse<AnlageResponseApi>, textStatus?: string, jqxhr?: JQueryXHR) => void,
		errorCallback: (jqxhr: JQueryXHR, textStatus?: string, errorThrown?: string) => void): void {

		T.Utils.Auth.getBearerToken((token: string) => {
			const url = T.Utils.Helper.appendURIPath(T.Utils.Application.getApi(), this.getApiMembershipUrl());
			const options = {
				method: 'POST',
				url: url,
				headers: {
					Authorization: `Bearer ${token}`
				},
				data: JSON.stringify(reqModel.toRequestObject())
			};
			AppUtils.Ajax.fragment(options, successCallback, errorCallback);
		});
	}

	public checkAdvertisingAllowed(errorCallback: (jqxhr: JQueryXHR, textStatus?: string, errorThrown?: string) => void): void {
		T.Utils.Auth.getBearerToken((token: string) => {
			AppUtils.Ajax.fragment({
				method: 'POST',
				headers: {
					Authorization: `Bearer ${token}`
				},
				url: this.applicationUrlBuilder.fullUrl(this.options.app.apicheckadvertising)
			}, () => {
				// no need to handle
			}, errorCallback);
		});
	}

	private getApiMembershipUrl(): string {
		if (this.options.app.issalesman) {
			return this.options.app.apisalesman;
		}

		return this.options.app.apimembershipextend;
	}

	/**
	 * /api/MglTarif
	 */
	public makeRateRequest(options: { [key: string]: any },
		successCallback: (data: ApiResponse<ContractInfoApi>, textStatus?: string, jqxhr?: JQueryXHR) => void,
		errorCallback?: (jqxhr: JQueryXHR, textStatus?: string, errorThrown?: string) => void): void {
		let args = {
			method: 'GET',
			async: false,
			url: this.applicationUrlBuilder.fullUrl(this.options.app.apirate),
		};
		args = $.extend({}, args, options);

		AppUtils.Ajax.fragment(args, successCallback, errorCallback);
	}

	/**
	 * api/mitgliedschaftgutschein/pruefen
	 */
	public checkBonuscode(bonuscode: MitgliedschaftGutscheinApi,
		successCallback: (data: ApiResponse<boolean>, textStatus?: string, jqxhr?: JQueryXHR) => void,
		errorCallback: (jqxhr: JQueryXHR, textStatus?: string, errorThrown?: string) => void): void {
		const options = {
			method: 'POST',
			url: T.Utils.Helper.appendURIPath(T.Utils.Application.getApi(), this.options.app.apicheckbonuscode),
			data: JSON.stringify(bonuscode.toRequestObject())
		};

		AppUtils.Ajax.fragment(options, successCallback, errorCallback);
	}

	public checkSalesmanNumbers(
		reqModel: ProfiwerberViewModelApi,
		successCallback: () => void,
		errorCallback: (jqxhr: JQuery.jqXHR, textStatus?: JQuery.Ajax.ErrorTextStatus, errorThrown?: string) => void): void {
		const options = {
			method: 'POST',
			url: T.Utils.Helper.appendURIPath(T.Utils.Application.getApi(), this.options.app.apisalesmancheck),
			data: reqModel.toJsonString()
		};

		AppUtils.Ajax.fragment(options, successCallback, errorCallback);
	}

	public getWalletAppUrl(successCallback: (data: ApiResponse<UrlResponseModelApi>) => void,
		errorCallback: (jqxhr: JQueryXHR, textStatus?: string, errorThrown?: string) => void): void {
		T.Utils.Auth.getBearerToken((token: string) => {
			const url = this.applicationUrlBuilder.fullUrl(this.options.app.apiwalleturl);
			const options = {
				method: 'GET',
				url: url,
				headers: {
					Authorization: `Bearer ${token}`
				},
				data: {
					tag: "mitgliedschaft"
				},
			};
			AppUtils.Ajax.fragment(options, successCallback, errorCallback);
		});
	}

	public getWalletQrCode(successCallback: (data: ApiResponse<QrCodeResponseModelApi>) => void,
		errorCallback: (jqxhr: JQueryXHR, textStatus?: string, errorThrown?: string) => void): JQueryXHR {
		let res: JQueryXHR;
		T.Utils.Auth.getBearerToken((token: string) => {
			const url = this.applicationUrlBuilder.fullUrl(this.options.app.apiwalletqrcode);
			const options = {
				method: 'GET',
				url: url,
				headers: {
					Authorization: `Bearer ${token}`
				},
				data: {
					tag: "mitgliedschaft"
				},
			};
			res = AppUtils.Ajax.fragment(options, successCallback, errorCallback);
		});
		return res;
	}

	public updateWalletPass(reqModel: RequestModelApi, successCallback: (data: ApiResponse<JsonResponseModelApi>) => void,
		errorCallback: (jqxhr: JQueryXHR, textStatus?: string, errorThrown?: string) => void): JQueryXHR {
		let res: JQueryXHR;
		T.Utils.Auth.getBearerToken((token: string) => {
			const url = this.applicationUrlBuilder.fullUrl(this.options.app.apimglpassjson);
			const options = {
				method: 'POST',
				url: url,
				headers: {
					Authorization: `Bearer ${token}`
				},
				data: JSON.stringify(reqModel.toRequestObject())
			};
			res = AppUtils.Ajax.fragment(options, successCallback, errorCallback);
		});
		return res;
	}

	public checkWalletMglPass(successCallback: (data: ApiResponse<JsonResponseModelApi>) => void,
		errorCallback: (jqxhr: JQueryXHR, textStatus?: string, errorThrown?: string) => void): void {
		T.Utils.Auth.getBearerToken((token: string) => {
			const url = this.applicationUrlBuilder.fullUrl(this.options.app.apimglpassjson);
			const options = {
				method: 'GET',
				url: url,
				headers: {
					Authorization: `Bearer ${token}`
				}
			};
			AppUtils.Ajax.fragment(options, successCallback, errorCallback);
		});
	}

	public checkMglWbs(wbs: string, successCallback: (data: ApiResponse<QrCodeResponseModelApi>) => void,
		errorCallback: (jqxhr: JQueryXHR, textStatus?: string, errorThrown?: string) => void): JQueryXHR {
		return AppUtils.Ajax.fragment({
			method: 'GET',
			data: {
				werbeschluessel: wbs
			},
			url: this.applicationUrlBuilder.fullUrl(this.options.app.apicheckmglwbs)
		}, successCallback, errorCallback);
	}
}
